import { useEffect, useState } from 'react';
import { errorMessage } from '../../lib/errorMessage';
import { Plus, Trash2, X } from 'lucide-react';

import { useSession } from '../../app/useSession';
import { useTeamMember } from '../../app/useTeamMember';
import { useToast } from '../../components/Toast';
import { cn } from '../../lib/cn';
import {
  useCatalogTags,
  useSoftDeleteTag,
  useUpsertTag,
  type CatalogTagRow,
  type TagInput,
} from './catalogTagsQueries';
import styles from './settings.module.css';

const COLOR_OPTIONS: { label: string; value: string }[] = [
  { label: 'אדום', value: 'red' },
  { label: 'כתום', value: 'orange' },
  { label: 'צהוב', value: 'yellow' },
  { label: 'ירוק', value: 'green' },
  { label: 'כחול', value: 'blue' },
  { label: 'סגול', value: 'purple' },
  { label: 'ורוד', value: 'pink' },
  { label: 'אפור', value: 'gray' },
];

function slugify(text: string): string {
  return text
    .trim()
    .toLowerCase()
    .replace(/[֐-׿]/g, (ch) => `he-${ch.charCodeAt(0).toString(16)}`)
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
}

export function CatalogTagsPage() {
  const session = useSession();
  const sessionObj = session.status === 'signed-in' ? session.session : null;
  const userId = sessionObj?.user.id ?? null;
  const { data: teamMember } = useTeamMember(sessionObj);
  const orgId = teamMember?.organization_id ?? null;
  const toast = useToast();

  const { data: tags = [], isLoading } = useCatalogTags(orgId);
  const upsert = useUpsertTag(orgId, userId);
  const softDelete = useSoftDeleteTag(orgId);

  const [editing, setEditing] = useState<CatalogTagRow | null>(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<TagInput>({ name: '', slug: '', color: 'blue' });
  const [slugTouched, setSlugTouched] = useState(false);

  useEffect(() => {
    if (open && !editing) {
      setForm({ name: '', slug: '', color: 'blue' });
      setSlugTouched(false);
    } else if (open && editing) {
      setForm({ name: editing.name, slug: editing.slug, color: editing.color ?? 'blue' });
      setSlugTouched(true);
    }
  }, [open, editing]);

  function close() {
    setOpen(false);
    setEditing(null);
  }

  async function save() {
    if (!form.name.trim() || !form.slug.trim()) {
      toast.error('שם ו-slug הם שדות חובה');
      return;
    }
    try {
      await upsert.mutateAsync({ id: editing?.id ?? null, values: form });
      toast.success(editing ? 'התגית עודכנה' : 'התגית נוצרה');
      close();
    } catch (err) {
      const msg = err instanceof Error && err.message.toLowerCase().includes('unique')
        ? 'slug כבר קיים'
        : 'לא הצלחנו לשמור';
      toast.error(msg);
    }
  }

  async function remove(row: CatalogTagRow) {
    if (!window.confirm(`למחוק את "${row.name}"?`)) return;
    try {
      await softDelete.mutateAsync(row.id);
      toast.success('התגית נמחקה');
    } catch (err) {
      toast.error(`לא הצלחנו למחוק · ${errorMessage(err)}`);
    }
  }

  return (
    <>
      <div className={styles.pageHead}>
        <h1>תגיות</h1>
        <p className={styles.pageSub}>תיוג רוחבי על לידים, לקוחות, פרויקטים ומסמכים. כל תגית צבועה ובעלת slug ייחודי.</p>
      </div>

      <div className={styles.card}>
        <div className={styles.cardHead}>
          <h3>רשימת תגיות</h3>
          <button type="button" className={styles.btnPrimary} onClick={() => { setEditing(null); setOpen(true); }}>
            <Plus size={16} aria-hidden="true" />
            תגית חדשה
          </button>
        </div>

        {isLoading ? (
          <p className={styles.notice}>טוען...</p>
        ) : tags.length === 0 ? (
          <p className={styles.notice}>אין תגיות עדיין.</p>
        ) : (
          <table className={styles.dataTable}>
            <thead>
              <tr>
                <th>שם</th>
                <th>slug</th>
                <th>צבע</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {tags.map((t) => (
                <tr
                  key={t.id}
                  className={styles.dataRow}
                  onClick={() => { setEditing(t); setOpen(true); }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setEditing(t); setOpen(true);
                    }
                  }}
                >
                  <td>{t.name}</td>
                  <td><code dir="ltr">{t.slug}</code></td>
                  <td>
                    {t.color ? (
                      <span className={styles.colorChip} style={{ background: `var(--color-${t.color}, var(--color-${t.color}-100, #ddd))` }}>
                        {COLOR_OPTIONS.find((c) => c.value === t.color)?.label ?? t.color}
                      </span>
                    ) : <span className={styles.muted}>—</span>}
                  </td>
                  <td>
                    <button
                      type="button"
                      className={styles.btnGhost}
                      aria-label="מחק"
                      onClick={(e) => { e.stopPropagation(); void remove(t); }}
                    >
                      <Trash2 size={14} aria-hidden="true" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {open ? (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}
          onClick={close}
        >
          <div className={styles.card} style={{ maxWidth: 480, width: '100%' }} onClick={(e) => { e.stopPropagation(); }}>
            <div className={styles.cardHead}>
              <h3>{editing ? 'עריכת תגית' : 'תגית חדשה'}</h3>
              <button type="button" className={styles.btnGhost} onClick={close} aria-label="סגור">
                <X size={18} aria-hidden="true" />
              </button>
            </div>

            <div className={styles.field}>
              <label htmlFor="tag-name">שם</label>
              <input
                id="tag-name"
                type="text"
                className={styles.input}
                value={form.name}
                placeholder="VIP · דחוף · חוזר ..."
                onChange={(e) => {
                  const name = e.target.value;
                  setForm((cur) => ({ ...cur, name, slug: slugTouched ? cur.slug : slugify(name) }));
                }}
                autoFocus
              />
            </div>

            <div className={styles.field}>
              <label htmlFor="tag-slug">slug</label>
              <input
                id="tag-slug"
                type="text"
                className={styles.input}
                dir="ltr"
                value={form.slug}
                onChange={(e) => { setSlugTouched(true); setForm((cur) => ({ ...cur, slug: e.target.value })); }}
              />
            </div>

            <div className={styles.field}>
              <label>צבע</label>
              <div className={styles.colorPicker}>
                {COLOR_OPTIONS.map((c) => (
                  <button
                    key={c.value}
                    type="button"
                    className={cn(styles.colorOption, form.color === c.value && styles.colorOptionActive)}
                    style={{ background: `var(--color-${c.value}, var(--color-${c.value}-100, #ddd))` }}
                    onClick={() => { setForm((cur) => ({ ...cur, color: c.value })); }}
                    aria-label={c.label}
                    title={c.label}
                  />
                ))}
              </div>
            </div>

            <div className={styles.actions} style={{ marginBlockStart: 'var(--space-4)' }}>
              <button type="button" className={styles.btnSecondary} onClick={close} disabled={upsert.isPending}>בטל</button>
              <button type="button" className={styles.btnPrimary} onClick={() => { void save(); }} disabled={upsert.isPending}>שמור</button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
